import OpenSeadragon from 'openseadragon';
import React, { useEffect, useState, useRef } from 'react';

interface Ruler {
    id: string;
    initialX: number;
    initialY: number;
    initialPosition: OpenSeadragon.Point;
}

interface IProps {
    viewer: OpenSeadragon.Viewer | null;
    pixelsPerUnit: number;
    unitValue: string;
    initialWidth?: number;
    height?: number;
}

export const RulerTool = ({ viewer, pixelsPerUnit, unitValue, height = 30, initialWidth = 200 }: IProps) => {
    const [rulers, setRulers] = useState<Ruler[]>([]);

    // Adiciona régua ao clicar
    useEffect(() => {
        if (!viewer) {
            return;
        }

        const handleClick = (event: OpenSeadragon.CanvasClickEvent) => {
            if (event.quick) {
                const webPoint = new OpenSeadragon.Point(event.position.x, event.position.y);
                const viewportPoint = viewer.viewport.pointFromPixel(webPoint);

                const newRuler: Ruler = {
                    id: Date.now().toString(),
                    initialX: event.position.x,
                    initialY: event.position.y,
                    initialPosition: viewportPoint
                };

                setRulers([...rulers, newRuler]);
            }
        };

        viewer.addHandler('canvas-click', handleClick);

        return () => {
            viewer.removeHandler('canvas-click', handleClick);
        };
    }, [viewer, rulers]);

    const handleRulerDelete = (id: string) => {
        setRulers(rulers.filter(ruler => ruler.id !== id));
    };

    return (
        <div>
            {rulers.map(ruler => (
                <RulerOverlay
                    initialX={ruler.initialX}
                    initialY={ruler.initialY}
                    initialPosition={ruler.initialPosition}
                    initialWidth={initialWidth}
                    height={height}
                    key={ruler.id}
                    ruler={ruler}
                    viewer={viewer}
                    pixelsPerUnit={pixelsPerUnit}
                    unitValue={unitValue}
                    onDelete={handleRulerDelete}
                />
            ))}
        </div>
    );
};

interface RulerOverlayProps {
    initialX: number;
    initialY: number;
    initialPosition: OpenSeadragon.Point
    initialWidth: number;
    height: number;
    ruler: Ruler;
    viewer: OpenSeadragon.Viewer | null;
    pixelsPerUnit: number;
    unitValue: string;
    onDelete: (id: string) => void;
}

const RulerOverlay = ({ ruler, viewer, pixelsPerUnit, unitValue, onDelete, height, initialWidth, initialX, initialY, initialPosition }: RulerOverlayProps) => {
    const positionRealPicture = useRef({
        x: 0,
        y: 0,
        width: 0
    });

    const [displayValue, setDisplayValue] = useState('-');
    const [screenPosition, setScreenPosition] = useState({ x: -1000, y: -10000 }); //evitar flickr exibindo inicialmente fora da tela
    const [width, setWidth] = useState(initialWidth);
    const rulerRef = useRef<HTMLDivElement>(null);
    const isResizingRef = useRef(false);
    const resizeEdgeRef = useRef<'left' | 'right' | null>(null);
    const resizeStartXRef = useRef<number>(0);
    const resizeStartWidthRef = useRef<number>(0);
    const resizeStartLeftRef = useRef<number>(0);
    const isDraggingRef = useRef(false);
    const dragStartXRef = useRef<number>(0);
    const dragStartYRef = useRef<number>(0);

    const savePositionByValues = (x: number, y: number, width: number) => {
        if (!viewer) {
            return;
        }
        const screenPoint = new OpenSeadragon.Point(x, y);
        const realCoordinate = viewer.viewport.windowToImageCoordinates(screenPoint);

        const pointPlusWidth = new OpenSeadragon.Point(x + width, y);
        const realPointPlusWidth = viewer.viewport.windowToImageCoordinates(pointPlusWidth);
        const widthReal = realPointPlusWidth.x - realCoordinate.x;

        positionRealPicture.current = {
            x: realCoordinate.x,
            y: realCoordinate.y,
            width: widthReal
        };
    };

    useEffect(() => {
        if (!viewer) {
            return;
        }
        setScreenPosition({
            x: initialX,
            y: initialY
        });
        savePositionByValues(initialX, initialY, initialWidth);

        const updateByZoom = () => {
            const realImageCoordinates = positionRealPicture.current;
            const position = viewer.viewport.imageToViewerElementCoordinates(
                new OpenSeadragon.Point(realImageCoordinates.x, realImageCoordinates.y)
            );

            const widthViewer = viewer.viewport.imageToViewerElementCoordinates(
                new OpenSeadragon.Point(realImageCoordinates.x + realImageCoordinates.width, 0)
            ).x - viewer.viewport.imageToViewerElementCoordinates(new OpenSeadragon.Point(realImageCoordinates.x, 0)).x;

            setScreenPosition({
                x: position.x,
                y: position.y
            });

            setWidth(widthViewer);
        };

        viewer.addHandler('viewport-change', updateByZoom);

        return () => {
            viewer.removeHandler('viewport-change', updateByZoom);
        };
    }, [viewer]);

    // Calcula o valor da escala baseado na largura da régua
    useEffect(() => {
        if (!viewer || !width) {
            return;
        }

        const tiledImage = viewer.world.getItemAt(0);
        if (!tiledImage) {
            return;
        }

        const viewportWidth = viewer.viewport.deltaPointsFromPixels(
            new OpenSeadragon.Point(width, 0)
        ).x;

        const imageWidthInPixels = tiledImage.viewportToImageCoordinates(
            new OpenSeadragon.Point(viewportWidth, 0)
        ).x;

        const finalValue = (imageWidthInPixels / pixelsPerUnit).toFixed(2);
        setDisplayValue(finalValue);
    }, [viewer, pixelsPerUnit, width]);

    // Implementação de resize manual
    useEffect(() => {
        const element = rulerRef.current;
        if (!element || !viewer) {
            return;
        }

        /*const handleMouseDown = (e: MouseEvent) => {
            const rect = element.getBoundingClientRect();
            const isRightEdge = e.clientX > rect.right - 10;

            if (isRightEdge) {
                isResizingRef.current = true;
                e.stopPropagation();
                e.preventDefault();
                viewer.setMouseNavEnabled(false);
            }
        };*/

        const handleMouseDown = (e: MouseEvent) => {
            const rect = element.getBoundingClientRect();
            const threshold = 10; // pixels de sensibilidade

            const isLeftEdge = e.clientX >= rect.left && e.clientX <= rect.left + threshold;
            const isRightEdge = e.clientX >= rect.right - threshold && e.clientX <= rect.right;

            if (isLeftEdge || isRightEdge) {
                isResizingRef.current = true;
                resizeEdgeRef.current = isLeftEdge ? 'left' : 'right';

                resizeStartXRef.current = e.clientX;
                resizeStartWidthRef.current = rect.width;
                resizeStartLeftRef.current = rect.left;

                e.stopPropagation();
                e.preventDefault();
                viewer.setMouseNavEnabled(false);
            }
            else {
                isDraggingRef.current = true;
                dragStartXRef.current = e.clientX - rect.left;
                dragStartYRef.current = e.clientY - rect.top;

                viewer.setMouseNavEnabled(false);
            }
        };

        const handleMouseMove = (e: MouseEvent) => {
            const rect = element.getBoundingClientRect();
            const threshold = 10;

            const isLeftEdge = e.clientX >= rect.left && e.clientX <= rect.left + threshold;
            const isRightEdge = e.clientX >= rect.right - threshold && e.clientX <= rect.right;

            if (!isResizingRef.current) {
                element.style.cursor = (isLeftEdge || isRightEdge) ? 'ew-resize' : 'default';
            }
            else {
                let newWidth = width;

                if (resizeEdgeRef.current === 'right') {
                    newWidth = e.clientX - rect.left;
                    setWidth(newWidth);
                } else if (resizeEdgeRef.current === 'left') {
                    const deltaX = e.clientX - resizeStartXRef.current;
                    const newWidth = resizeStartWidthRef.current - deltaX;
                    const newX = resizeStartLeftRef.current + deltaX;

                    setWidth(newWidth);
                    setScreenPosition(prev => ({ ...prev, x: newX }));
                }
            }

            if (isDraggingRef.current) {
                e.stopPropagation();

                setScreenPosition({
                    x: e.clientX - dragStartXRef.current,
                    y: e.clientY - dragStartYRef.current
                });
            }

            /*const clampedWidth = Math.max(50, Math.min(800, newWidth));
            // Se a largura bateu no limite (clamped), não movemos o X
            if (clampedWidth !== width) {
                setWidth(clampedWidth);
                if (resizeEdgeRef.current === 'left') {
                    console.log('aaa', newX);
                    setScreenPosition(prev => ({ ...prev, x: newX }));
                }
            }*/
        };

        /*const handleMouseMove = (e: MouseEvent) => {
            const rect = element.getBoundingClientRect();
            const isRightEdge = e.clientX > rect.right - 10 && e.clientX <= rect.right;

            if (!isResizingRef.current) {
                element.style.cursor = isRightEdge ? 'ew-resize' : 'default';
                return;
            }

            e.stopPropagation();
            e.preventDefault();

            const newWidth = e.clientX - rect.left;
            const clampedWidth = Math.max(50, Math.min(800, newWidth));
            setWidth(clampedWidth);
        }; */

        const handleMouseUp = () => {
            isResizingRef.current = false;
            isDraggingRef.current = false;
            resizeEdgeRef.current = null;
            viewer.setMouseNavEnabled(true);
        };

        element.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            element.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [viewer, ruler.id, screenPosition.y, width]);

    useEffect(() => {
        savePositionByValues(screenPosition.x, screenPosition.y, width);
    }, [savePositionByValues, screenPosition.x, screenPosition.y, width]);

    return (
        <div
            ref={rulerRef}
            style={{
                position: 'absolute',
                left: `${screenPosition.x}px`,
                top: `${screenPosition.y}px`,
                width: `${width}px`,
                height: height,
                background: 'rgba(255, 0, 0, 0.3)',
                border: '2px solid red',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                userSelect: 'none',
                pointerEvents: 'auto'
            }}
        >
            {/* Botão de fechar */}
            <button
                onClick={() => onDelete(ruler.id)}
                style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '-10px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: 'red',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                }}
            >
                ×
            </button>

            {/* Valor da medida */}
            <span
                style={{
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    pointerEvents: 'none',
                    textShadow: '0 0 3px black'
                }}
            >
                {displayValue}{unitValue}
            </span>

            {/* Indicador de resize */}
            <div
                style={{
                    position: 'absolute',
                    right: '0',
                    top: '0',
                    bottom: '0',
                    width: '10px',
                    cursor: 'ew-resize',
                    background: 'rgba(255, 255, 255, 0.3)'
                }}
            />
        </div>
    );
};
